import { Injectable, signal, inject } from '@angular/core';
import { ExtranetService } from './extranet.service';
import { Property } from '../models/extranet.model';

@Injectable({ providedIn: 'root' })
export class PropertiesService {
  private extranetSvc = inject(ExtranetService);

  readonly apiProperties = signal<Property[]>([]);
  readonly selectedProperty = signal<Property | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  private static readonly STORAGE_KEY = 'hbx_selected_property';

  load() {
    this.loading.set(true);
    this.error.set(null);
    this.extranetSvc.listProperties().subscribe({
      next: (props) => {
        this.apiProperties.set(props);
        if (props.length > 0) {
          const savedId = localStorage.getItem(PropertiesService.STORAGE_KEY);
          const restored = savedId ? props.find(p => p.id === savedId) : null;
          this.selectedProperty.set(restored ?? props[0]);
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('[PropertiesService] listProperties failed:', err);
        this.error.set('Failed to load properties');
        this.loading.set(false);
      },
    });
  }

  /**
   * Re-reads one property and swaps it in the list and, when it is the selected one, in the
   * selection — for a screen that learns the cached copy is stale (WI 3669: an offline order
   * refused with `OFFLINE_NOT_ALLOWED_CM_CONNECTED` means the property is now connected to eZee,
   * so the list's hint must replace "Register Offline Order"). A failed read keeps the cache.
   */
  refresh(id: string): void {
    this.extranetSvc.getProperty(id).subscribe({
      next: (fresh) => {
        this.apiProperties.update(list => list.map(p => (p.id === id ? fresh : p)));
        if (this.selectedProperty()?.id === id) this.selectedProperty.set(fresh);
      },
      error: () => {},
    });
  }

  select(prop: Property) {
    this.selectedProperty.set(prop);
    if (prop.id) localStorage.setItem(PropertiesService.STORAGE_KEY, prop.id);
  }

  /**
   * Selects the listed property with this id, unless it is already the selected one — for a link
   * into a screen that reads the SELECTED property (WI 3671: a failed delivery of the "Connection
   * health" card opens its reservation, and the booking detail looks it up in the selected
   * property's bookings). An id that is not in the list changes nothing.
   */
  selectById(id: string): void {
    if (this.selectedProperty()?.id === id) return;
    const prop = this.apiProperties().find(p => p.id === id);
    if (prop) this.select(prop);
  }
}
